import * as THREE from "three";
import React, { Suspense, useEffect, useState, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import {
  PerspectiveCamera,
  Environment,
  MeshDistortMaterial,
  ContactShadows,
  useScroll,
  useTexture,
  useEnvironment,
} from "@react-three/drei";
import { useSpring } from "@react-spring/core";
import { a } from "@react-spring/three";
import contil from "../../../img/contil_logo1.png";
import tbf from "../../../img/tbf.png";
import agc from "../../../img/agc.png";
import cp59 from "../../../img/cp59.png";
import wh from "../../../img/wh.png";
import casablanca from "../../../img/casablanca.png";
import { colorType } from "./Color";
import { useStore } from "../store";
import { Globals } from "@react-spring/shared";
import HdrFile from "../../../img/env.hdr";


Globals.assign({
  frameLoop: "always",
});
// React-spring animates native elements, in this case <mesh/> etc,
// but it can also handle 3rd–party objs, just wrap them in "a".
const AnimatedMaterial = a(MeshDistortMaterial);

export default function Scene({ setBg }) {
  const { setPage, experienceContent } = useStore();
  const scroll = useScroll();
  const sphere = useRef();
  const light = useRef();
  const [mode, setMode] = useState(false);
  const [down, setDown] = useState(false);
  const [hovered, setHovered] = useState(false);
  const textures = useTexture([casablanca, wh, contil, tbf, agc]);
  const [currentPage, setCurrentPage] = useState(0);

  // Change cursor on hovered state
  useEffect(() => {
    document.body.style.cursor = hovered
      ? "none"
      : `url('data:image/svg+xml;base64,${btoa(
          `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="10" fill="${experienceContent[currentPage].titleColor}"/></svg>`
        )}'), auto`;
    return () => {
      document.body.style.cursor = null;
    };
  }, [hovered, currentPage,experienceContent]);

  // Make the bubble float and follow the mouse
  // This is frame-based animation, useFrame subscribes the component to the render-loop
  useFrame((state) => {
    const page = Math.round((scroll.scroll.current * scroll.pages) / 1);
    setCurrentPage(page);
    light.current.position.x = state.mouse.x * 20;
    light.current.position.y = state.mouse.y * 20;
    if (sphere.current) {
      sphere.current.position.x = THREE.MathUtils.lerp(
        sphere.current.position.x,
        hovered ? state.mouse.x / 2 : 0,
        0.2
      );
      sphere.current.position.y = THREE.MathUtils.lerp(
        sphere.current.position.y,
        Math.sin(state.clock.elapsedTime / 1.5) / 6 +
          (hovered ? state.mouse.y / 2 : 0),
        0.2
      );
    }
  });
  useEffect(() => {
    if (scroll?.scroll?.current && experienceContent) {
      setDown(currentPage % 2 === 0);
      setMode(currentPage % 2 !== 0);
      setPage(currentPage);
      let clone = {
        ...{
          background:
            experienceContent[currentPage].backgroundColor ?? "#f0f0f0",
          fill: experienceContent[currentPage].backgroundColor ?? "#202020",
          currentPage,
        },
      };
      setBg(clone);
    }
  }, [currentPage, experienceContent]);
  // Springs for color and overall looks, this is state-driven animation
  // React-spring is physics based and turns static props into animated values
  const [{ wobble, coat, color, ambient, env, currentTexture }] = useSpring(
    {
      wobble: down ? 1.2 : hovered ? 1.05 : 1,
      coat: mode && !hovered ? 0.04 : 1,
      ambient: mode && !hovered ? 1.5 : 0.5,
      env: mode && !hovered ? 0.4 : 1,
      color: hovered ? experienceContent[currentPage].titleColor: mode ? "white" : "white",
      // config: (n) => n === 'wobble' && hovered && { mass: 2, tension: 1000, friction: 10 },
      currentTexture:   textures[currentPage] ? textures[currentPage] : "",
    },
    [mode, hovered, down, currentPage, experienceContent]
  );

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, 4]} fov={75}>
        <a.ambientLight intensity={ambient} />
        <a.pointLight
          ref={light}
          position-z={-15}
          intensity={env}
          color={colorType[currentPage][0]}
        />
      </PerspectiveCamera>
      <Suspense fallback={null}>
        <a.mesh
          ref={sphere}
          scale={wobble}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          onPointerDown={() => setDown(true)}
          // onPointerUp={() => {
          //     setDown(false)
          //     // Toggle mode between dark and bright
          //     setMode(!mode)
          //     setBg({ background: !mode ? '#202020' : '#f0f0f0', fill: !mode ? '#f0f0f0' : '#202020' })
          // }}
        >
          <sphereBufferGeometry args={[1, 64, 64]} />
          <AnimatedMaterial
            color={color}
            envMapIntensity={env}
            clearcoat={coat}
            clearcoatRoughness={0}
            metalness={0.1}
            opacity={0.5}
            transparent
          ></AnimatedMaterial>
          <a.mesh position={[0, 0, 0]} scale={experienceContent[currentPage].scaleTexture} >
            <planeBufferGeometry args={[1.5, 1]} />
            <AnimatedMaterial
              envMapIntensity={env}
              clearcoat={coat}
              clearcoatRoughness={0}
              metalness={0.001}
              side={THREE.DoubleSide}
              map={currentTexture}
              opacity={1}
              transparent
            />
          </a.mesh>
        </a.mesh>
        <Environment 
                // files={suspend(envHdr)}
                files={HdrFile}
                 />
        <ContactShadows
          rotation={[Math.PI / 2, 0, 0]}
          position={[0, -1.6, 0]}
          opacity={mode ? 0.8 : 0.4}
          width={15}
          height={15}
          blur={2.5}
          far={1.6}
        />
      </Suspense>
    </>
  );
}
